import api from '@/api/employee';
import formatSecondsToHours from '@/utils/formatSecondsToHours'
import {
    ROLE_OPERATOR,
    ROLE_CANDIDATE,
    ROLE_SUPER_ADMIN,
    ROLE_CURATOR
} from "@/constants/roles";
import {
    EMPLOYEE_STATUS_APPROVED
} from '@/constants/employee';

export default {
    state: {
        employee: null,
        rating: null,
        auditErrors: null,
        commonInfoSipCodes: null,
        commonInfoSalaryPlansTypes: null,
        commonInfoOfferPlansTypes: null,
        products: null,
    },
    getters: {
        getEmployeeStatus: (state) => state.employee && state.employee.userAttributes && state.employee.userAttributes.status,
        getEmployeeRoles: (state) => state.employee && state.employee.userAttributes && state.employee.userAttributes.role,
        isEmployeeOperator(state) {
            if (!state.employee) return false;
            const {
                role
            } = state.employee.userAttributes;
            return role && role.includes(ROLE_SUPER_ADMIN) || role.includes(ROLE_OPERATOR);
        },
        isEmployeeEducationAllowed(state) {
            if (!state.employee) return false;
            const {
                role,
                status
            } = state.employee.userAttributes;
            if (role && role.includes(ROLE_SUPER_ADMIN)) return true;
            return role && (role.includes(ROLE_OPERATOR) || role.includes(ROLE_CURATOR) || (role.includes(ROLE_CANDIDATE) && status == EMPLOYEE_STATUS_APPROVED));
        },
        isEmployeeCandidate(state) {
            if (!state.employee) return false;
            const {
                role
            } = state.employee.userAttributes;
            return role && role.includes(ROLE_CANDIDATE);
        },
        getEmployee: (state) => state.employee && state.employee.employee,
        getUserAttributes: (state) => state.employee && state.employee.userAttributes,
        getEmployeeValidate: (state) => state.employee && state.employee.employee && state.employee.employee.is_validate,
        getEmployeeStage: (state) => state.employee && state.employee.employee && state.employee.employee.stage,
        getEmployeeLang: (state) => state.employee && state.employee.userAttributes && state.employee.userAttributes.locale && state.employee.userAttributes.locale.split('-')[1].toLowerCase(),
        getEmployeeTotalTime: state => {
            const {
                rating
            } = state;

            if (!rating || !rating.month_stat) return;

            const {
                time
            } = rating.month_stat;

            return formatSecondsToHours(time);
        },
        getBarChartsSchema(state) {
            const {
                rating
            } = state;

            if (!rating) return;
            let {
                product_approves,
                products
            } = rating;

            const markerNames = [window.Vocabulary.mine, window.Vocabulary.country]

            return [{
                data: product_approves,
                titleKey: 'approvesForCurrentMonth',
                markerNames,
                config: {
                    fill: {
                        type: "solid"
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                        }
                    },
                    grid: {
                        xaxis: {
                            lines: {
                                show: true
                            }
                        },
                        yaxis: {
                            lines: {
                                show: false
                            },
                        }
                    },
                    xaxis: {
                        type: products.length ? 'category' : 'numeric',
                        categories: products,
                    },
                    yaxis: {
                        labels: {
                            style: {
                                color: '#23252E'
                            }
                        }
                    },
                }
            }]
        },
        getCommonInfoSipCodes: (state) => state.commonInfoSipCodes,
        getCommonProductsNames: (state) => state.products,
        getCommonCountriesNames: (state) => state.countries,
        getTrainingTopics: (state) => state.employee && state.employee.trainingTopics,
    },
    mutations: {
        SET_EMPLOYEE_DATA(state, payload) {
            state.employee = payload;
        },
        SET_EMPLOYEE_RATING_DATA(state, payload) {
            state.rating = payload;
        },
        SET_EMPLOYEE_AUDIT_ERRORS(state, payload) {
            state.auditErrors = payload;
        },
        SET_COMMON_INFO_SIP_CODES(state, payload) {
            state.commonInfoSipCodes = payload;
        },
        SET_COMMON_INFO_SALARY_PLANS_TYPES(state, payload) {
            state.commonInfoSalaryPlansTypes = payload;
        },
        SET_COMMON_INFO_OFFER_PLANS_TYPES(state, payload) {
            state.commonInfoOfferPlansTypes = payload;
        },
        SET_PRODUCTS_DATA(state, payload) {
            state.products = payload;
        },
        SET_COUNTRIES_DATA(state, payload) {
            state.countries = payload;
        },
        SET_COMMON_LANGUAGES_DATA(state, payload) {
            state.commonLanguages = payload;
        },
        SET_TRAINING_TOPICS_DATA(state, payload) {
            state.trainingTopics = payload;
        }
    },
    actions: {
        setEmployeeData({
            commit,
            dispatch
        }, payload) {
            commit('SET_EMPLOYEE_DATA', payload);
            dispatch('setIsLoadingData', false);
        },
        fetchEmployeeRating({
            commit,
            dispatch,
        }) {
            dispatch('setIsLoadingData', true);
            api.getRating().then(({
                data: {
                    data: {
                        rating
                    }
                }
            }) => {
                commit('SET_EMPLOYEE_RATING_DATA', rating);
            }).finally(() => {
                dispatch('setIsLoadingData', false);
            });
        },
        async fetchEmployeeCommonInfo({
            commit
        }, vm) {
            try {
                const result = await api.getCommonInfo();
                const {
                    common_info
                } = result.data.data;
                
                commitCommonInfoData(commit, common_info, vm);
            } catch (error) {
                throw error;
            }
        },
        async fetchIframeCommonInfo({
            commit
        }, vm) {
            try {
                const result = await api.getIframeCommonInfo();
                const {
                    common_info
                } = result.data.data;
                commitCommonInfoData(commit, common_info, vm);
            } catch (error) {
                throw error;
            }
        }
    },
}

function commitCommonInfoData(commit, common_info, vm) {
    commit("SET_ORDER_TYPES", common_info['order-types'], {
        root: true
    });
    commit("SET_COURIER_REQUESTS_DATA", common_info['courier-requests'], {
        root: true
    });
    commit('SET_TIMEZONES_DATA', common_info.timezones, {
        root: true
    })
    commit('SET_LANG_DATA', common_info.phrases, {
        root: true
    });
    commit('SET_EMPLOYEE_AUDIT_ERRORS', {
        ['audit-errors']: common_info['audit-errors'],
        ['audit-error-category']: common_info['audit-error-category'],
    }, {
        root: true
    });

    commit('SET_COMMON_INFO_SIP_CODES', common_info['sip_codes']);
    commit('SET_COMMON_INFO_SALARY_PLANS_TYPES', common_info['salary-plans-types']);
    commit('SET_COMMON_INFO_OFFER_PLANS_TYPES', common_info['offer-plans-types']);
    commit("SET_PRODUCTS_DATA", common_info.products);
    commit("SET_COUNTRIES_DATA", common_info.countries);
    commit("SET_COMMON_LANGUAGES_DATA", common_info.languages);
    commit("SET_TRAINING_TOPICS_DATA", common_info['trainings']);
    const locale = window.localStorage.getItem('locale');

    vm.$i18n.add(locale, common_info.phrases);
    vm.$i18n.set(locale);
}